import { AnyAction } from '@reduxjs/toolkit';
import _isEmpty from 'lodash/isEmpty';
import _uniq from 'lodash/uniq';
import type { CustomSection } from 'types/reducers/resumeBuilder';
import { v4 as uuidv4 } from 'uuid';

import { RESUME_BUILDER_INITIAL_STATE } from 'store/initialStates/resumeBuilder';

const generateCustomSection = ({ sectionId, sectionName }: CustomSection) => ({
  title: sectionName || '',
  url: `custom-${sectionId}`,
  pageTitle: `Add ${sectionName} - Zippia`,
});

export const ACTION_TYPES = {
  SET_RB_FLOW: 'SET_RB_FLOW',
  THEME_SET: 'THEME_SET',

  DATA_UPDATE: 'DATA_UPDATE',
  DATA_KEY_DELETE: 'DATA_KEY_DELETE',
  DATA_KEYS_DELETE: 'DATA_KEYS_DELETE',
  DATA_KEYS_UPDATE: 'DATA_KEYS_UPDATE',

  DATA_KEY_IN_QUEUE_SET: 'DATA_KEY_IN_QUEUE_SET',
  DATA_KEY_IN_QUEUE_DELETE: 'DATA_KEY_IN_QUEUE_DELETE',

  PREVIEW_MODAL_OPEN: 'PREVIEW_MODAL_OPEN',
  PREVIEW_MODAL_CLOSE: 'PREVIEW_MODAL_CLOSE',

  ERRORS_SET: 'ERRORS_SET',

  PREVIEW_STEP_COMPLETED_SECTIONS_MODAL_OPEN: 'PREVIEW_STEP_COMPLETED_SECTIONS_MODAL_OPEN',
  PREVIEW_STEP_COMPLETED_SECTIONS_MODAL_CLOSE: 'PREVIEW_STEP_COMPLETED_SECTIONS_MODAL_CLOSE',
  PREVIEW_STEP_COMPLETED_SECTIONS_MODAL_UPDATE: 'PREVIEW_STEP_COMPLETED_SECTIONS_MODAL_UPDATE',

  SET_MY_RESUME_PAGE: 'SET_MY_RESUME_PAGE',
  TEMPLATE_PRESET_CHOSEN: 'TEMPLATE_PRESET_CHOSEN',
  TEMPLATE_PRESET_SELECTED: 'TEMPLATE_PRESET_SELECTED',
  TEMPLATE_IN_MOBILE_CAROUSEL: 'TEMPLATE_IN_MOBILE_CAROUSEL',

  SET_RESUME_ID: 'SET_RESUME_ID',

  UPDATE_SQUARE_CONFIG_DATA: 'UPDATE_SQUARE_CONFIG_DATA',
  UPDATE_PROFESSIONAL_SUMMARY: 'UPDATE_PROFESSIONAL_SUMMARY',

  UPDATE_TOP_RESUME_FLAG: 'UPDATE_TOP_RESUME_FLAG',

  UPDATE_RB_SUB_STATE: 'UPDATE_RB_SUB_STATE',

  UPSERT_CUSTOM_SECTION: 'UPSERT_CUSTOM_SECTION',
  REMOVE_CUSTOM_SECTION: 'REMOVE_CUSTOM_SECTION',
  UPSERT_DATA_INTO_CUSTOM_SECTION: 'UPSERT_DATA_INTO_CUSTOM_SECTION',
  UPDATE_CUSTOM_SECTION_ORDER: 'UPDATE_CUSTOM_SECTION_ORDER',
};

const resumeBuilderReducer = (state = RESUME_BUILDER_INITIAL_STATE, action: AnyAction) => {
  const { payload = {} } = action;
  const currentStepIndex = state.flow.currentStepIndex;
  const currentStep = state.resumeBuilderState[currentStepIndex];
  const subSteps = currentStep?.subSteps;

  const customSectionIndex =
    Object.values(subSteps).find(
      (subStep) => subStep?.url === `custom-${payload?.customSection?.sectionId || ''}`
    )?.index ||
    Object.keys(subSteps).length ||
    3;

  switch (action.type) {
    case ACTION_TYPES.UPDATE_PROFESSIONAL_SUMMARY:
      return {
        ...state,
        professionalSummary: {
          ...state.professionalSummary,
          ...payload,
        },
      };
    case ACTION_TYPES.SET_RB_FLOW:
      return {
        ...state,
        flow: {
          ...state.flow,
          visitedSteps: _uniq([...state.flow.visitedSteps, state.flow.currentStepIndex]),
          currentStepIndex: payload.nextStepIndex,
          currentSubStepIndex: payload.nextSubStepIndex,
          nextBtnLabel: payload.nextBtnLabel,
        },
      };

    case ACTION_TYPES.THEME_SET:
      localStorage.setItem('rb-color-theme', payload.theme);
      return {
        ...state,
        theme: payload.theme,
        resumeBuilderState: {
          ...state.resumeBuilderState,
          2: {
            ...state.resumeBuilderState[2],
            data: {
              ...state.resumeBuilderState[2].data,
              theme: payload.theme,
            },
          },
        },
      };

    case ACTION_TYPES.DATA_UPDATE:
      return {
        ...state,
        resumeBuilderState: {
          ...state.resumeBuilderState,
          [currentStepIndex]: {
            ...currentStep,
            data: {
              ...currentStep.data,
              [payload.dataName]: {
                ...currentStep.data[payload.dataName],
                [payload.dataKey]: {
                  ...((currentStep.data[payload.dataName] || {})[payload.dataKey] || {}),
                  [payload.field]: payload.value,
                },
              },
            },
          },
        },
      };

    case ACTION_TYPES.DATA_KEY_IN_QUEUE_SET:
      return {
        ...state,
        resumeBuilderState: {
          ...state.resumeBuilderState,
          [currentStepIndex]: {
            ...currentStep,
            dataKeyInQueue: payload.dataKeyInQueue,
          },
        },
      };

    case ACTION_TYPES.DATA_KEY_DELETE:
      const { dataName, dataKey } = payload;
      const dataKeys = currentStep.data[dataName] || {};
      const { [dataKey]: value, ...filteredData } = dataKeys;
      return {
        ...state,
        resumeBuilderState: {
          ...state.resumeBuilderState,
          [currentStepIndex]: {
            ...currentStep,
            data: {
              ...currentStep.data,
              [payload.dataName]: filteredData,
            },
          },
        },
      };

    case ACTION_TYPES.DATA_KEYS_DELETE: {
      const { dataName, dataKeys } = payload;
      const _dataKeys = currentStep.data[dataName] || {};
      const filteredData = { ..._dataKeys };

      dataKeys.forEach((key) => delete filteredData[key]);

      return {
        ...state,
        resumeBuilderState: {
          ...state.resumeBuilderState,
          [currentStepIndex]: {
            ...currentStep,
            data: {
              ...currentStep.data,
              [payload.dataName]: filteredData,
            },
          },
        },
      };
    }

    case ACTION_TYPES.UPSERT_CUSTOM_SECTION:
      const customSectionId = uuidv4();
      return {
        ...state,
        resumeBuilderState: {
          ...state.resumeBuilderState,
          [currentStepIndex]: {
            ...currentStep,
            subSteps: {
              ...subSteps,
              [customSectionIndex]: {
                ...(subSteps?.[customSectionIndex] || {}),
                index: customSectionIndex,
                title: payload.customSection?.sectionName || '',
                url: `custom-${payload.customSection?.sectionId || customSectionId}`,
                pageTitle: `Add ${payload.customSection?.sectionName || ''} - Zippia`,
              },
            },
            data: {
              ...(currentStep.data || {}),
              customSections: {
                ...(currentStep?.data?.customSections || {}),
                [payload.customSection?.sectionId]: {
                  sectionId: payload.customSection?.sectionId || '',
                  sectionName: payload.customSection?.sectionName || '',
                  details: '',
                },
              },
            },
          },
        },
      };

    case ACTION_TYPES.REMOVE_CUSTOM_SECTION:
      return {
        ...state,
        resumeBuilderState: {
          ...state.resumeBuilderState,
          8: {
            ...state.resumeBuilderState[8],
            data: {
              ...state.resumeBuilderState[8].data,
              customSections: Object.values(state.resumeBuilderState[8].data.customSections || {})
                .filter(
                  (customSection) => customSection.sectionId !== payload.customSection?.sectionId
                )
                .reduce(
                  (prev, curr) => ({
                    ...prev,
                    [curr.sectionId]: curr,
                  }),
                  {}
                ),
            },
            subSteps: Object.values(state.resumeBuilderState[8].subSteps)
              .filter((subStep) => subStep.url !== `custom-${payload.customSection?.sectionId}`)
              .sort((a, b) => a.index - b.index)
              .reduce(
                (prev, curr, idx) => ({
                  ...prev,
                  [idx]: curr,
                }),
                {}
              ),
          },
        },
      };

    case ACTION_TYPES.UPSERT_DATA_INTO_CUSTOM_SECTION:
      if (!payload.customSection?.sectionId) return state;
      let customSections = { ...(currentStep?.data?.customSections || {}) };
      customSections = {
        ...customSections,
        [payload.customSection?.sectionId]: {
          ...(customSections[payload.customSection?.sectionId] || {}),
          ...payload.customSection,
        },
      };
      return {
        ...state,
        resumeBuilderState: {
          ...state.resumeBuilderState,
          [currentStepIndex]: {
            ...currentStep,
            data: {
              ...currentStep.data,
              customSections,
            },
          },
        },
      };

    case ACTION_TYPES.DATA_KEYS_UPDATE:
      return {
        ...state,
        resumeBuilderState: {
          ...state.resumeBuilderState,
          [currentStepIndex]: {
            ...currentStep,
            data: {
              ...currentStep.data,
              [payload.dataName]: payload.dataKeys,
            },
          },
        },
      };

    case ACTION_TYPES.PREVIEW_MODAL_OPEN:
      return {
        ...state,
        previewModalInfo: {
          ...state.previewModalInfo,
          open: true,
          template: payload.template,
        },
      };

    case ACTION_TYPES.PREVIEW_MODAL_CLOSE:
      return {
        ...state,
        previewModalInfo: {
          ...state.previewModalInfo,
          open: false,
          template: '',
        },
      };

    case ACTION_TYPES.PREVIEW_STEP_COMPLETED_SECTIONS_MODAL_OPEN:
      return {
        ...state,
        previewStepCompletedSectionsModal: {
          ...state.previewStepCompletedSectionsModal,
          open: true,
        },
      };

    case ACTION_TYPES.PREVIEW_STEP_COMPLETED_SECTIONS_MODAL_CLOSE:
      return {
        ...state,
        previewStepCompletedSectionsModal: {
          ...state.previewStepCompletedSectionsModal,
          open: false,
        },
      };

    case ACTION_TYPES.PREVIEW_STEP_COMPLETED_SECTIONS_MODAL_UPDATE:
      return {
        ...state,
        previewStepCompletedSectionsModal: {
          ...state.previewStepCompletedSectionsModal,
          filledSteps: payload.filledSteps,
          unfilledSteps: payload.unfilledSteps,
        },
      };

    case ACTION_TYPES.ERRORS_SET:
      return {
        ...state,
        resumeBuilderState: {
          ...state.resumeBuilderState,
          [currentStepIndex]: {
            ...currentStep,
            errorFields: payload,
          },
        },
      };

    case ACTION_TYPES.SET_MY_RESUME_PAGE:
      return {
        ...state,
        myResumePage: payload.myResumePage,
      };

    case ACTION_TYPES.TEMPLATE_PRESET_CHOSEN:
      return {
        ...state,
        templatePresetChosen: payload.templatePresetChosen,
      };

    case ACTION_TYPES.TEMPLATE_PRESET_SELECTED:
      return {
        ...state,
        templatePresetSelected: payload.templatePresetSelected,
      };

    case ACTION_TYPES.TEMPLATE_IN_MOBILE_CAROUSEL:
      return {
        ...state,
        templateInMobileCarousel: payload.templateInMobileCarousel,
      };

    case ACTION_TYPES.SET_RESUME_ID:
      return {
        ...state,
        id: payload.id,
      };

    case ACTION_TYPES.UPDATE_SQUARE_CONFIG_DATA:
      return {
        ...state,
        squareConfigData: {
          ...state.squareConfigData,
          ...action.payload,
        },
      };

    case ACTION_TYPES.UPDATE_TOP_RESUME_FLAG:
      return {
        ...state,
        topResumeFlag: payload,
      };

    case ACTION_TYPES.UPDATE_RB_SUB_STATE:
      return {
        ...state,
        ...payload,
      };

    case ACTION_TYPES.UPDATE_CUSTOM_SECTION_ORDER:
      const newCustomSteps = Object.values(payload).map(generateCustomSection);
      const newSteps = [...Object.values(subSteps).slice(0, 3), ...newCustomSteps].reduce(
        (acc, item, index) => ({ ...acc, [index]: { ...item, index } }),
        {}
      );

      return {
        ...state,
        resumeBuilderState: {
          ...state.resumeBuilderState,
          [currentStepIndex]: {
            ...currentStep,
            subSteps: newSteps,
            data: {
              ...currentStep.data,
              customSections: { ...payload },
            },
          },
        },
      };

    default:
      return state;
  }
};

export default resumeBuilderReducer;
